import { apiStorageSmpStaticsEmojisDataRetrieve } from "@smp/api";
import { REACT_APP__URL_STORAGE_STATIC, unicodeFromUnified } from "@smp/utils";
import produce from "immer";
import { useCallback, useMemo } from "react";
import { useQuery } from "react-query";
import { useLocalStorage } from "react-use";

export const LOCAL_STORAGE_KEY_FREQUENTLY = "ep_emoji_picker_frequently";
export const LOCAL_STORAGE_KEY_COLLAPSED = "ep_emoji_picker_collapsed";

const escapeRegExp = (str: string) =>
  str.replace(/[-[\]{}()*+?.,;:&\\^$#\s]/g, "\\$&");

export const useEmoji = ({
  enabled,
  frequentlyEnabled,
}: {
  enabled: boolean;
  frequentlyEnabled?: boolean;
}) => {
  const [lsFrequently, setLsFrequently] = useLocalStorage<
    Record<string, number>
  >(LOCAL_STORAGE_KEY_FREQUENTLY);

  const [lsCollapsed, setLsCollapsed] = useLocalStorage<string[]>(
    LOCAL_STORAGE_KEY_COLLAPSED,
  );

  const { data, isLoading } = useQuery(
    "storage.smp-statics.emojis.data",
    apiStorageSmpStaticsEmojisDataRetrieve,
    {
      enabled,
    },
  );

  const frequentlySet = useMemo(
    () =>
      !frequentlyEnabled || !lsFrequently
        ? []
        : Object.keys(lsFrequently).sort(
            (k1, k2) => lsFrequently[k2] - lsFrequently[k1],
          ),
    [frequentlyEnabled, lsFrequently],
  );

  const unifiedSet = useMemo(() => data && Object.keys(data.emojis), [data]);

  const unifiedSetMap = useMemo(
    () =>
      unifiedSet &&
      unifiedSet.reduce((acc, unified) => {
        if (unified.includes("-fe0f")) {
          acc.set(unified.replaceAll("-fe0f", ""), unified);
        }

        return acc;
      }, new Map() as Map<string, string>),
    [unifiedSet],
  );

  const dataExtended = useMemo(
    () =>
      data &&
      unifiedSetMap &&
      produce(data, (draft) => {
        if (frequentlySet.length) {
          draft.categories["Frequently used"] = frequentlySet;
        }

        unifiedSetMap.forEach((value, key) => {
          const emojiData = draft.emojis[value];

          draft.emojis[key] = emojiData;
          draft.categories[emojiData.category].push(key);
        });
      }),
    [data, frequentlySet, unifiedSetMap],
  );

  const search = useMemo(
    () =>
      unifiedSet
        ? new RegExp(
            escapeRegExp(
              [
                ...unifiedSet,
                ...(unifiedSetMap ? Array.from(unifiedSetMap.keys()) : []),
              ]
                .sort((code1, code2) =>
                  (code1.match(/-/g)?.length || 0) >
                  (code2.match(/-/g)?.length || 0)
                    ? -1
                    : 1,
                )
                .map(unicodeFromUnified)
                .join("|"),
            ),
            "gi",
          )
        : undefined,
    [unifiedSet, unifiedSetMap],
  );

  const getImageUrl = useCallback(
    (unified: string, size?: 240 | 120 | 64 | 48 | 32) => {
      size = size || 32;

      if (dataExtended?.emojis[unified]) {
        const unifiedMapped = unifiedSetMap?.get(unified) || unified;

        return `${REACT_APP__URL_STORAGE_STATIC}/emojis/v4/apple/${size}/${unifiedMapped}.png`;
      }
    },
    [dataExtended?.emojis, unifiedSetMap],
  );

  const frequentlyInc = useCallback(
    (unified: string) => {
      setLsFrequently({
        ...lsFrequently,
        [unified]: (lsFrequently?.[unified] || 0) + 1,
      });
    },
    [lsFrequently, setLsFrequently],
  );

  const toggleCollapse = useCallback(
    (category: string) => {
      const newValue = lsCollapsed?.includes(category)
        ? lsCollapsed?.filter((c) => c !== category)
        : [...(lsCollapsed || []), category];

      setLsCollapsed(newValue);
    },
    [lsCollapsed, setLsCollapsed],
  );

  return {
    data: dataExtended,
    collapsedCategories: lsCollapsed || [],
    getImageUrl,
    isLoading,
    search,
    frequentlyInc,
    toggleCollapse,
  };
};
